import type { CountryInfo } from '@/services/api/ultraadmin'
import {
  EMPTY_SCHOOL_LIST_FILTER,
  SCHOOL_CURRENCIES,
  SCHOOL_LANGUAGES,
  schoolListFilterChips,
  type SchoolListFilterValue,
} from './schoolListFilter'

const selectClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white'

type Props = {
  value: SchoolListFilterValue
  onChange: (next: SchoolListFilterValue) => void
  countries: CountryInfo[]
  resultCount?: number
}

export function SchoolListFilters({ value, onChange, countries, resultCount }: Props) {
  const chips = schoolListFilterChips(value, countries)
  const patch = (partial: Partial<SchoolListFilterValue>) => onChange({ ...value, ...partial })
  const clearKey = (key: keyof SchoolListFilterValue) => {
    if (key === 'channel') patch({ channel: 'all' })
    else patch({ [key]: '' })
  }

  return (
    <div className="space-y-3 mb-4">
      <div className="flex flex-wrap gap-2 items-center">
        {(['all', 'direct', 'partner'] as const).map((channel) => (
          <button
            key={channel}
            type="button"
            onClick={() => patch({ channel })}
            className={`text-xs px-3 py-1 rounded-full border ${
              value.channel === channel
                ? 'bg-indigo-50 border-indigo-200 text-indigo-800 font-semibold'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            {channel === 'all' ? 'Alle' : channel === 'direct' ? 'Direkt' : 'Partner'}
          </button>
        ))}
        {resultCount !== undefined && (
          <span className="text-xs text-gray-500">{resultCount} Schulen</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block text-xs text-gray-500">
          Name
          <input
            className={`${selectClass} mt-1`}
            value={value.search}
            onChange={(e) => patch({ search: e.target.value })}
            placeholder="Name oder Kurzname"
          />
        </label>
        <label className="block text-xs text-gray-500">
          Land
          <select className={`${selectClass} mt-1`} value={value.country} onChange={(e) => patch({ country: e.target.value })}>
            <option value="">Alle Länder</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-500">
          Sprache
          <select className={`${selectClass} mt-1`} value={value.language} onChange={(e) => patch({ language: e.target.value })}>
            <option value="">Alle Sprachen</option>
            {SCHOOL_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-500">
          Währung
          <select className={`${selectClass} mt-1`} value={value.currency} onChange={(e) => patch({ currency: e.target.value })}>
            <option value="">Alle Währungen</option>
            {SCHOOL_CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => clearKey(chip.key)}
              className="text-xs px-3 py-1 rounded-full border bg-indigo-50 border-indigo-200 text-indigo-800"
            >
              {`${chip.label} ×`}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange(EMPTY_SCHOOL_LIST_FILTER)}
            className="text-xs px-3 py-1 rounded-lg border border-gray-300 bg-white text-gray-700"
          >
            Filter leeren
          </button>
        </div>
      )}
    </div>
  )
}
